import React, { useState } from 'react';
import { Box, Collapse, Divider, Typography } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PaymentSummaryRow, PaymentTotalRow, PaymentMethodTitle, PaymentConfirmButton } from './style';
import PaymentMethodSelector from './PaymentMethodSelector';
import { formatPriceWithNoUnit } from '@/utils/formatPrice';
import ErrorSubmitBar from '@/components/ErrorSubmitBar';
import useStoreApp from '@/store';
import { useTourBookingContext } from '@/containers/TourBookingByCustomer/providers/useTourBookingContext';
import { Trans } from 'react-i18next';

const PaymentSummarySection = ({
  t,
  storePromotion,
  selectedPayment,
  setSelectedPayment,
  bookingErrMsg,
  paymentMethods,
  onConfirm,
  disabled,
  selectedPaymentMethodRef,
}: any) => {

  const { isEmbeed } = useStoreApp(state => state.globalSlice);
  const [isExpanded, setIsExpanded] = useState(true);

  const { tourDetail, bookingTourData, durationOptions, computedPricing } = useTourBookingContext();

  const tourPriceOptionTitle = durationOptions?.find((option) => option.id === bookingTourData.tourPriceOptionId)?.label;

  const tourPricing = computedPricing?.tour_pricing;
  const minPassenger = tourPricing?.minPassengers ?? 1;
  const additionalPassenger = bookingTourData.passengerNormal - minPassenger;

  return (
    <>
      {/* Clickable Total Row - toggles expand/collapse */}
      <PaymentTotalRow
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{ cursor: 'pointer' }}
      >
        <Typography variant="h2" fontWeight={400} color="#C2883A">{t('common:totalPayment')}</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h2" fontWeight={700} color="#C2883A">{formatPriceWithNoUnit(computedPricing?.money)} vnđ</Typography>
          <KeyboardArrowUpIcon
            sx={{
              color: 'black.main',
              fontSize: 24,
              transition: 'transform 0.3s ease-in-out',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
      </PaymentTotalRow>

      {/* Collapsible Payment Details */}
      <Collapse in={isExpanded}>
        <Box display={'flex'} flexDirection={'column'} gap={1.5} sx={{ mt: 2 }}>
          {/* Tour Price Option Selected */}
          <PaymentSummaryRow sx={{ alignItems: 'start' }}>
            <Typography variant="h2" fontWeight={400} flex={1}>{t('common:tourDuration')}</Typography>
            <Typography variant="h2" fontWeight={600} flex={2} align='right' lineHeight={1.5}>{tourPriceOptionTitle ?? ''}</Typography>
          </PaymentSummaryRow>
          {/* Min Passenger Price */}
          <PaymentSummaryRow>
            <Typography variant="h2" fontWeight={400}>
              <Trans i18nKey="common:min_passengers_price" values={{ value: minPassenger }} />
            </Typography>
            <Typography variant="h2" fontWeight={600}>{formatPriceWithNoUnit(tourPricing?.defaultPrice)} vnđ</Typography>
          </PaymentSummaryRow>

          {/* Additional Passenger Price */}
          {
            additionalPassenger > 0 ? (
              <PaymentSummaryRow>
                <Typography variant="h2" fontWeight={400} flex={1} align='left'>
                  <Trans
                    i18nKey="common:additional_passenger_title"
                    values={{ adults: additionalPassenger }}
                    components={{
                      span1: <Typography fontWeight={600} />
                    }}
                  />
                </Typography>

                <Typography variant="h2" fontWeight={600} flex={2} align='right'>{formatPriceWithNoUnit(computedPricing?.additional_passenger_price)} vnđ</Typography>

              </PaymentSummaryRow>
            ) : null
          }

          {/* Old Tour Price */}

          {/*          
          
          <PaymentSummaryRow>
            <Typography variant="h2" fontWeight={400}>{t('common:tourPrice')}</Typography>
            <Typography variant="h2" fontWeight={600}>{formatPriceWithNoUnit(computedPricing?.price)} vnđ</Typography>
          </PaymentSummaryRow>
          
          */}

          {/* Additional Service Price */}
          <PaymentSummaryRow>
            <Typography variant="h2" fontWeight={400}>{t('common:additionalServiceTitle')}</Typography>
            <Typography variant="h2" fontWeight={600}>{formatPriceWithNoUnit(computedPricing?.additional_price)} vnđ</Typography>
          </PaymentSummaryRow>
          {storePromotion && (
            <PaymentSummaryRow>
              <Typography variant="h2" fontWeight={400} color="error.main">{t('common:discount')}</Typography>
              <Typography variant="h2" fontWeight={600} color="error.main">-{formatPriceWithNoUnit(computedPricing?.discount)} vnđ</Typography>
            </PaymentSummaryRow>
          )}
          {computedPricing != null && computedPricing?.visitPlaceTicketFee > 0 ? (
            <PaymentSummaryRow>
              <Typography variant="h2" fontWeight={400} flex={1}>{t('common:visitPlaceTicketFees')}</Typography>
              <Typography variant="h2" fontWeight={600} flex={1} align='right'>{formatPriceWithNoUnit(computedPricing?.visitPlaceTicketFee)} vnđ</Typography>
            </PaymentSummaryRow>
          ) : null}
        </Box>
      </Collapse>

      <Divider sx={{ marginTop: 2.5, marginBottom: 3 }} />
      <PaymentMethodTitle ref={selectedPaymentMethodRef}>{t('common:selectPayment')}</PaymentMethodTitle>
      <PaymentMethodSelector
        t={t}
        paymentMethods={paymentMethods}
        selectedPayment={selectedPayment}
        setSelectedPayment={setSelectedPayment}
      />
      {
        bookingErrMsg && (
          <ErrorSubmitBar
            error={true}
            errorText={bookingErrMsg}
            buttonText={t('common:confirm')}
            isShowButton={false}
          />
        )
      }
      <PaymentConfirmButton
        type='submit'
        variant="contained"
        fullWidth
        onClick={() => onConfirm(selectedPayment)}
        disabled={disabled}
        sx={{
          borderRadius: isEmbeed ? "6px" : "999px",
        }}
      >
        {selectedPayment === 'PAY_LATER' ? t('common:confirm').toUpperCase() : t('common:payNow').toUpperCase()}
      </PaymentConfirmButton>

      <Typography variant="body2" textAlign="center" sx={theme => ({ marginTop: 2, color: theme.palette.black.main, ...theme.typography.body2 })}>
        {t('common:warning_fee_when_using_payment_gateways')}
      </Typography>
    </>
  )
};

export default PaymentSummarySection; 